import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Star, Play, X, Info, Film as FilmIcon, Clapperboard, Trophy, TrendingUp, Sparkles, ExternalLink, MessageCircle, Send } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import AdDisplay from '../components/AdDisplay';
import { getMediaUrl } from '../utils/mediaUrl';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { SearchSection, WhereToWatchSection, NewsSection, FreeFilmsSection } from '../components/discoverSections';
import FranchisesSection from '../components/FranchisesSection';
import EShopSection from '../components/EShopSection';
import StarRating from '../components/reactions/StarRating';
import CommentsModal from '../components/reactions/CommentsModal';
import EsimSection from '../components/EsimSection';
import MyMouvSection from '../components/MyMouvSection';
import MyJobSection from '../components/MyJob/MyJobSection';
import DiscoverBurger from '../components/discover/DiscoverBurger';
import { useSectionsVisibility } from '../hooks/useSectionsVisibility';
import AppShortcutsSection from '../components/discover/AppShortcutsSection';
import DiscoverStoriesRow from '../components/discover/DiscoverStoriesRow';

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  // If path already has /api prefix, use it as is
  // Otherwise, add /api prefix for proper routing
  let apiPath = path;
  if (!path.startsWith('/api/')) {
    apiPath = path.replace('/uploads/', '/api/uploads/');
  }
  return getMediaUrl(apiPath);
};

const Films = () => {
  const [films, setFilms] = useState([]);
  const [popCulture, setPopCulture] = useState([]);
  const [bestFilms, setBestFilms] = useState([]);
  const [boxOffice, setBoxOffice] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();
  const sections = useSectionsVisibility();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    loadAllContent();
    // Refetch when the app language changes so admin-curated content
    // (pop culture, best films, box office) is shown in the right language.
  }, [language]); // eslint-disable-line

  // Back-links `/films?tab=studio` (MyShop, eSIM, MyMouv, MyJob) → scroll to the merged Studio sections
  useEffect(() => {
    if (loading || searchParams.get('tab') !== 'studio') return;
    // Sections below load their own data → re-scroll a few times while the page grows
    const timers = [200, 900, 1800, 3000].map((ms) => setTimeout(() => {
      const el = document.getElementById('studio-sections');
      if (el) el.scrollIntoView({ behavior: ms < 500 ? 'smooth' : 'auto', block: 'start' });
    }, ms));
    return () => timers.forEach(clearTimeout);
  }, [loading]); // eslint-disable-line

  const loadAllContent = async () => {
    setLoading(true);
    try {
      const [filmsRes, popCultureRes, bestFilmsRes, boxOfficeRes] = await Promise.all([
        axios.get('/api/films', { params: { lang: language } }).catch(() => ({ data: [] })),
        axios.get('/api/pop-culture', { params: { lang: language } }).catch(() => ({ data: [] })),
        axios.get('/api/best-films', { params: { lang: language } }).catch(() => ({ data: [] })),
        axios.get('/api/box-office', { params: { lang: language } }).catch(() => ({ data: [] }))
      ]);
      
      setFilms(Array.isArray(filmsRes.data) ? filmsRes.data : []);
      setPopCulture(Array.isArray(popCultureRes.data) ? popCultureRes.data : []);
      setBestFilms(Array.isArray(bestFilmsRes.data) ? bestFilmsRes.data : []);
      setBoxOffice(Array.isArray(boxOfficeRes.data) ? boxOfficeRes.data : []);
    } catch (error) {
      console.error('Error loading content:', error);
      setFilms([]);
      setPopCulture([]);
      setBestFilms([]);
      setBoxOffice([]);
    } finally {
      setLoading(false);
    }
  };

  const openDetail = (item, type) => {
    setSelectedItem(item);
    setSelectedType(type);
  };

  return (
    <div className={`min-h-screen text-white pb-20 ${isDark ? 'bg-black' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent mb-2">
              {t('discover.title')}
            </h1>
            <p className="text-gray-400 mb-4">{t('discover.subtitle')}</p>
          </div>
          {sections.homeHidden && <div className="pt-1"><DiscoverBurger /></div>}
        </div>

        {/* Search bar — relocated from Explorer between subtitle and upcoming releases */}
        <SearchSection />

        {/* Ad */}
        <AdDisplay zone="films" />

        {loading ? (
          <div className="text-center py-20 text-gray-400">{t('common.loading')}</div>
        ) : (
          <div className="space-y-10">
            {/* v50.10.8c — Stories (abonnés + profils populaires, toutes langues) entre la recherche et les sorties cinéma */}
            <DiscoverStoriesRow />

            {/* Section 1: Prochaines sorties cinéma */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <Calendar className="w-6 h-6 text-yellow-500" />
                <h2 className="text-2xl font-bold">{t('discover.upcomingReleases')}</h2>
              </div>
              
              {films.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FilmIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune sortie à venir</p>
                </div>
              ) : (
                <div className="overflow-x-auto pb-4">
                  <div className="flex space-x-4" style={{ minWidth: 'max-content' }}>
                    {films.map((film) => (
                      <button
                        key={film.id}
                        onClick={() => openDetail(film, 'film')}
                        className="group cursor-pointer text-left flex-shrink-0 w-40"
                      >
                        <div className="aspect-[2/3] bg-white/5 rounded-xl overflow-hidden mb-2 relative border border-white/10 group-hover:border-yellow-500/50 transition-all">
                          {film.poster_url ? (
                            <>
                              <img 
                                src={getImageUrl(film.poster_url)} 
                                alt={film.title} 
                                className="w-full h-full object-cover absolute inset-0"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling?.classList.remove('hidden');
                                }}
                              />
                              <div className="hidden w-full h-full flex items-center justify-center bg-gradient-to-br from-yellow-500/20 to-orange-600/20">
                                <Play className="w-12 h-12 text-white/50" />
                              </div>
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10">
                                <div className="text-center">
                                  <Info className="w-8 h-8 text-white mx-auto mb-1" />
                                  <span className="text-xs font-semibold">Détails</span>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-yellow-500/20 to-orange-600/20">
                              <Play className="w-12 h-12 text-white/50" />
                            </div>
                          )}
                        </div>
                        <h3 className="font-semibold group-hover:text-yellow-400 transition-colors text-sm line-clamp-3">{film.title}</h3>
                        {film.release_date && (
                          <div className="text-xs text-gray-500 mt-1">
                            {new Date(film.release_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Where to watch — moved from Explorer, between upcoming releases and pop culture */}
            <WhereToWatchSection />

            {/* Section 3: Meilleurs films et séries */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <Trophy className="w-6 h-6 text-amber-500" />
                <h2 className="text-2xl font-bold">{t('discover.bestFilms')}</h2>
              </div>
              
              {bestFilms.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Trophy className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucun contenu</p>
                </div>
              ) : (
                <div className="overflow-x-auto pb-4">
                  <div className="flex space-x-4" style={{ minWidth: 'max-content' }}>
                    {bestFilms.map((item) => (
                      <div
                        key={item.id}
                        className="group text-left flex-shrink-0 w-40"
                      >
                        <button
                          onClick={() => openDetail(item, 'best_film')}
                          className="w-full text-left"
                        >
                          <div className="aspect-[2/3] bg-white/5 rounded-xl overflow-hidden mb-2 relative border border-white/10 group-hover:border-amber-500/50 transition-all">
                            {item.image_url ? (
                              <img 
                                src={getImageUrl(item.image_url)} 
                                alt={item.title} 
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-500/20 to-orange-600/20">
                                <Trophy className="w-12 h-12 text-white/50" />
                              </div>
                            )}
                          </div>
                          <h3 className="font-semibold truncate group-hover:text-amber-400 transition-colors text-sm">{item.title}</h3>
                          {item.year && (
                            <div className="text-xs text-gray-500 mt-1">{item.year}</div>
                          )}
                        </button>
                        {item.external_url && (
                          <a
                            href={item.external_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-1 text-xs text-amber-400 hover:text-amber-300 mt-1 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="w-3 h-3" />
                            <span>{t('discover.learnMore')}</span>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Section 4: Box office mondial */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <TrendingUp className="w-6 h-6 text-green-500" />
                <h2 className="text-2xl font-bold">Box office mondial</h2>
              </div>
              
              {boxOffice.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucun contenu</p>
                </div>
              ) : (
                <div className="overflow-x-auto pb-4">
                  <div className="flex space-x-4" style={{ minWidth: 'max-content' }}>
                    {boxOffice.map((item, index) => (
                      <button
                        key={item.id}
                        onClick={() => openDetail(item, 'box_office')}
                        className="group cursor-pointer text-left flex-shrink-0 w-40"
                      >
                        <div className="aspect-[2/3] bg-white/5 rounded-xl overflow-hidden mb-2 relative border border-white/10 group-hover:border-green-500/50 transition-all">
                          {item.image_url ? (
                            <img 
                              src={getImageUrl(item.image_url)} 
                              alt={item.title} 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-500/20 to-emerald-600/20">
                              <TrendingUp className="w-12 h-12 text-white/50" />
                            </div>
                          )}
                          {/* Rank badge */}
                          <div className="absolute top-2 left-2 bg-green-500 text-black font-bold text-sm px-2 py-1 rounded-lg">
                            #{item.rank || index + 1}
                          </div>
                        </div>
                        <h3 className="font-semibold truncate group-hover:text-green-400 transition-colors text-sm">{item.title}</h3>
                        {item.revenue > 0 && (
                          <p className="text-xs text-green-400 mt-0.5">
                            {(item.revenue / 1000000).toFixed(0)} M$
                          </p>
                        )}
                        {/* Community rating stars (v50.13) */}
                        <div onClick={(e) => e.stopPropagation()} className="mt-1">
                          <StarRating contentType="box_office" contentId={item.id} lang={language} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Franchises, Studios & Streaming — below Where To Watch */}
            <FranchisesSection />

            {/* Section 2: Pop culture, mangas, théâtre */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <Sparkles className="w-6 h-6 text-pink-500" />
                <h2 className="text-2xl font-bold">{t('discover.popCulture')}</h2>
              </div>
              
              {popCulture.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Sparkles className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune actualité</p>
                </div>
              ) : (
                <div className="overflow-x-auto pb-4">
                  <div className="flex space-x-4" style={{ minWidth: 'max-content' }}>
                    {popCulture.map((item) => (
                      <div
                        key={item.id}
                        className="group text-left flex-shrink-0 w-64"
                      >
                        <button
                          onClick={() => openDetail(item, 'pop_culture')}
                          className="w-full text-left"
                        >
                          <div className="aspect-video bg-white/5 rounded-xl overflow-hidden mb-2 relative border border-white/10 group-hover:border-pink-500/50 transition-all">
                            {item.image_url ? (
                              <img 
                                src={getImageUrl(item.image_url)} 
                                alt={item.title} 
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-500/20 to-purple-600/20">
                                <Sparkles className="w-12 h-12 text-white/50" />
                              </div>
                            )}
                          </div>
                          <h3 className="font-semibold group-hover:text-pink-400 transition-colors text-sm mb-1 line-clamp-3">{item.title}</h3>
                          <p className="text-xs text-gray-400 line-clamp-6">{item.description}</p>
                        </button>
                        {item.external_url && (
                          <a
                            href={item.external_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-1 text-xs text-pink-400 hover:text-pink-300 mt-2 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="w-3 h-3" />
                            <span>{t('discover.learnMore')}</span>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* News — moved from Explorer, just below pop culture */}
            <NewsSection />

            {/* Free Films — moved from Explorer, below box office */}
            <FreeFilmsSection />

            {/* v50.10.8 — ex-sous-onglet Studio fusionné ici, après les films libres de droit */}
            <div id="studio-sections" className="space-y-10" data-testid="studio-sections">
              <EsimSection />
              <EShopSection />
              {sections.mymouv && <MyMouvSection />}
              {sections.myjob && <MyJobSection />}
            </div>

            {/* v50.10.8 — raccourcis colorés vers les autres onglets */}
            <AppShortcutsSection />
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <DetailModal 
          item={selectedItem} 
          type={selectedType} 
          onClose={() => {
            setSelectedItem(null);
            setSelectedType(null);
          }}
          onRefreshFilms={loadAllContent}
        />
      )}

      <Navigation />
    </div>
  );
};

const DetailModal = ({ item, type, onClose, onRefreshFilms }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const keyboardHeight = useKeyboardHeight();
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [ratings, setRatings] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [ratingsCount, setRatingsCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => { setImgFailed(false); }, [item?.id]);

  useEffect(() => {
    if (type === 'film' && item?.id) {
      loadRatings();
    }
  }, [item?.id, type]);

  const loadRatings = async () => {
    try {
      const res = await axios.get(`/api/films/${item.id}/ratings`);
      setRatings(res.data.ratings || []);
      setAvgRating(res.data.average || 0);
      setRatingsCount(res.data.count || 0);
      // Check if current user already rated
      if (user) {
        const myRating = (res.data.ratings || []).find(r => r.user_id === user.id);
        if (myRating) {
          setUserRating(myRating.rating);
          setComment(myRating.comment || '');
        }
      }
    } catch (e) {}
  };

  const handleSubmitRating = async () => {
    if (!user) { toast.error('Connectez-vous pour noter'); return; }
    if (userRating === 0) { toast.error('Choisissez une note'); return; }
    setSubmitting(true);
    try {
      await axios.post(`/api/films/${item.id}/ratings`, { rating: userRating, comment });
      toast.success('Note enregistrée !');
      loadRatings();
      if (onRefreshFilms) onRefreshFilms();
    } catch (e) {
      toast.error('Erreur lors de la notation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWatchTrailer = () => {
    if (item.trailer_url) {
      window.open(item.trailer_url, '_blank', 'noopener,noreferrer');
    }
  };

  const getTypeColor = () => {
    switch(type) {
      case 'film': return 'from-yellow-400 to-orange-400';
      case 'pop_culture': return 'from-pink-400 to-purple-400';
      case 'best_film': return 'from-amber-400 to-orange-400';
      case 'box_office': return 'from-green-400 to-emerald-400';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  const imageUrl = item.poster_url || item.image_url;

  return (
    <div 
      className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-full w-full flex items-start justify-center p-4 py-8">
        <div 
          className="bg-gray-900 border border-white/10 rounded-3xl max-w-4xl w-full relative"
          onClick={(e) => e.stopPropagation()}
          style={{ marginBottom: keyboardHeight > 0 ? keyboardHeight + 20 : 0 }}
        >
          <div className="relative">
            {/* Header Image — only render when imageUrl is present AND loads. */}
            {imageUrl && !imgFailed && (
              <div className="relative h-48 sm:h-64 rounded-t-3xl overflow-hidden">
                <img
                  src={getImageUrl(imageUrl)}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  onError={() => setImgFailed(true)}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
              </div>
            )}

            {/* Close button — always visible, positioned over header or top-right of modal. */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-3 bg-black/70 hover:bg-black/90 backdrop-blur-sm rounded-full transition-all z-10 border border-white/20 hover:border-yellow-500/50"
              title="Fermer"
              data-testid="detail-modal-close"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>

          <div className="p-6 sm:p-8">
            <h2 className={`text-4xl font-bold mb-4 bg-gradient-to-r ${getTypeColor()} bg-clip-text text-transparent`}>
              {item.title}
            </h2>
            
            {/* Meta info for films */}
            {type === 'film' && (
              <>
                <div className="flex flex-wrap items-center gap-4 mb-6">
                  {item.community_rating > 0 && (
                    <div className="flex items-center space-x-1">
                      <Star className="w-5 h-5 text-yellow-500" fill="currentColor" />
                      <span className="font-semibold">{item.community_rating.toFixed(1)}</span>
                    </div>
                  )}
                  {item.release_date && (
                    <span className="text-gray-400">
                      {new Date(item.release_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  )}
                  {item.duration_minutes && (
                    <span className="text-gray-400">{item.duration_minutes} min</span>
                  )}
                </div>

                {/* Genres */}
                {item.genres && item.genres.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {item.genres.map((genre, i) => (
                      <span key={i} className="px-3 py-1 bg-yellow-500/10 text-yellow-400 rounded-full text-sm">
                        {genre}
                      </span>
                    ))}
                  </div>
                )}

                {/* Synopsis */}
                {(item.synopsis || item.description) && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-3">Synopsis</h3>
                    <p className="text-gray-300 leading-relaxed">{item.synopsis || item.description}</p>
                  </div>
                )}

                {/* Director & Cast */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {item.director && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-400 mb-2">Réalisateur</h3>
                      <p className="text-white">{item.director}</p>
                    </div>
                  )}
                  {item.cast && item.cast.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-400 mb-2">Distribution</h3>
                      <p className="text-white">{item.cast.slice(0, 3).join(', ')}</p>
                    </div>
                  )}
                </div>

                {/* Trailer */}
                {item.trailer_url && (
                  <button 
                    onClick={handleWatchTrailer}
                    className="w-full px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-full font-semibold transition-all flex items-center justify-center space-x-2 mb-6"
                  >
                    <Play className="w-5 h-5" fill="white" />
                    <span>Regarder la bande-annonce sur YouTube</span>
                  </button>
                )}

                {/* Community Ratings Section */}
                <div className="border-t border-white/10 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-yellow-500" />
                      {t('ratings.communityTitle')}
                    </h3>
                    {ratingsCount > 0 && (
                      <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-500" fill="currentColor" />
                        <span className="font-bold text-lg">{avgRating}</span>
                        <span className="text-gray-400 text-sm">({ratingsCount} {t('ratings.reviewsShort')})</span>
                      </div>
                    )}
                  </div>

                  {/* Rating Form */}
                  {user && (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
                      <p className="text-sm text-gray-400 mb-3">{t('ratings.yourRating')}</p>
                      <div className="flex items-center gap-1 mb-3">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            onClick={() => setUserRating(star)}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            className="p-1 transition-transform hover:scale-110"
                            data-testid={`star-rating-${star}`}
                          >
                            <Star 
                              className={`w-8 h-8 transition-colors ${
                                star <= (hoverRating || userRating) 
                                  ? 'text-yellow-500' 
                                  : 'text-gray-600'
                              }`}
                              fill={star <= (hoverRating || userRating) ? 'currentColor' : 'none'}
                            />
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          onFocus={(e) => {
                            setTimeout(() => {
                              e.target.closest('.bg-gray-900')?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                            }, 300);
                          }}
                          placeholder={t('ratings.commentPlaceholder')}
                          className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-yellow-500"
                          onKeyDown={(e) => e.key === 'Enter' && handleSubmitRating()}
                          data-testid="film-comment-input"
                        />
                        <button
                          onClick={handleSubmitRating}
                          disabled={submitting || userRating === 0}
                          className="px-4 py-2 bg-yellow-500 text-black rounded-full text-sm font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50"
                          data-testid="submit-rating-btn"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Ratings List */}
                  {ratings.length > 0 ? (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {ratings.slice(0, 10).map(r => (
                        <div key={r.id} className="flex gap-3 p-3 bg-white/5 rounded-xl">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {r.user?.profile_image_url ? (
                              <img src={getMediaUrl(r.user.profile_image_url)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs font-bold">{r.user?.name?.[0]?.toUpperCase() || '?'}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">{r.user?.name || t('ratings.anonUser')}</span>
                              <div className="flex">
                                {[1,2,3,4,5].map(s => (
                                  <Star key={s} className={`w-3 h-3 ${s <= r.rating ? 'text-yellow-500' : 'text-gray-600'}`} fill={s <= r.rating ? 'currentColor' : 'none'} />
                                ))}
                              </div>
                            </div>
                            {r.comment && <p className="text-sm text-gray-300 mt-1">{r.comment}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-4 text-sm">{t('ratings.beFirst')}</p>
                  )}
                </div>
              </>
            )}

            {/* Meta info for pop culture */}
            {type === 'pop_culture' && (
              <>
                {item.description && (
                  <div className="mb-6">
                    <p className="text-gray-300 leading-relaxed whitespace-pre-line">{item.description}</p>
                  </div>
                )}
                {item.external_url && (
                  <a
                    href={item.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-full font-semibold transition-all"
                  >
                    <ExternalLink className="w-5 h-5" />
                    <span>{t('discover.learnMore')}</span>
                  </a>
                )}
              </>
            )}

            {/* Meta info for best films */}
            {type === 'best_film' && (
              <>
                <div className="flex flex-wrap items-center gap-4 mb-6">
                  {item.year && (
                    <span className="px-3 py-1 bg-amber-500/10 text-amber-400 rounded-full text-sm">
                      {item.year}
                    </span>
                  )}
                  {item.content_type && (
                    <span className="px-3 py-1 bg-amber-500/10 text-amber-400 rounded-full text-sm capitalize">
                      {item.content_type === 'film' ? 'Film' : 'Série'}
                    </span>
                  )}
                </div>
                {item.external_url && (
                  <a
                    href={item.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 rounded-full font-semibold transition-all"
                  >
                    <ExternalLink className="w-5 h-5" />
                    <span>{t('discover.learnMore')}</span>
                  </a>
                )}
              </>
            )}

            {/* Meta info for box office */}
            {type === 'box_office' && (
              <>
                <div className="flex flex-wrap items-center gap-3 mb-6">
                  {item.rank && (
                    <span className="px-4 py-2 bg-green-500 text-black font-bold rounded-full">
                      #{item.rank} au Box Office
                    </span>
                  )}
                  {item.vote_average > 0 && (
                    <div className="flex items-center space-x-1">
                      <Star className="w-5 h-5 text-yellow-500" fill="currentColor" />
                      <span className="font-semibold">{item.vote_average}</span>
                    </div>
                  )}
                </div>
                {item.revenue > 0 && (
                  <div className="mb-4 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                    <p className="text-sm text-gray-400">Recettes mondiales</p>
                    <p className="text-2xl font-bold text-green-400">
                      {(item.revenue / 1000000).toFixed(1)} M$
                    </p>
                    {item.budget > 0 && (
                      <p className="text-xs text-gray-500 mt-1">Budget: {(item.budget / 1000000).toFixed(0)} M$</p>
                    )}
                  </div>
                )}
                {item.release_date && (
                  <p className="text-gray-400 mb-4">
                    Sortie: {new Date(item.release_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Films;
